import React from "react";
import { Howl } from "howler";
import $ from "jquery";

import { Button } from "./../../components/Button/Button";
import { Video } from "./../../components/Video/Video";
import { Image } from "./../../components/Image/Image";
import { SoundStrato } from "./../../components/SoundStrato/SoundStrato";

import "./quote.scss";
import "./gallery.scss";
import "./list.scss";
import "./paragraph.scss";
import "./griglia-loghi.scss";
import "./play-button.scss";
import "./newsletter.scss";

import { ReactComponent as Play } from "./../../assets/play.svg";
import { ReactComponent as Pause } from "./../../assets/pause.svg";
import { useTranslation } from "react-i18next";

const Quote = ({ quote }) => {
	const theme =
		quote.options.dark_mode && quote.options.dark_mode === `dark` ? `is-dark` : `is-light`;

	return (
		<section
			className={[
				`quote`,
				`quote--${quote.options.full_height ? `is-full` : `not-full`}`,
				`quote--${theme}`,
			].join(` `)}>
			<div className={`quote-content`}>
				{quote.title && (
					<h3
						className={[`_${quote.size}`, `centered`].join(` `)}
						dangerouslySetInnerHTML={{ __html: quote.title }}
					/>
				)}
				{quote.text && (
					<div
						className={[`_base`, `centered`].join(` `)}
						dangerouslySetInnerHTML={{ __html: quote.text }}
					/>
				)}
				{quote.button.label && <Button {...quote.button} />}
			</div>
		</section>
	);
};

const Gallery = ({ slides }) => {
	const handleLayout = (slide, index) => {
		switch (slide.acf_fc_layout) {
			case `video`:
				return <Video key={index} {...slide} />;

			case `image`:
				return <Image key={index} {...slide} />;

			default:
				return false;
		}
	};

	return (
		<div className={`gallery`}>
			{slides &&
				slides.length &&
				slides.map((slide, index) => (
					<div key={index} className={`gallery-slide`}>
						{handleLayout(slide, index)}
						<div className={`gallery-slide-caption`}>
							<p className={`_base centered`} dangerouslySetInnerHTML={{ __html: slide.label }} />
						</div>
					</div>
				))}
		</div>
	);
};

export const Paragraph = ({ intro, text, credits, button }) => {
	return (
		<div className={`paragraph`}>
			{intro && <h3 className={`_medium`} dangerouslySetInnerHTML={{ __html: intro }} />}
			{text &&
				text.length > 0 &&
				text.map((contents, index) => (
					<div key={index} className={`paragraph-text`}>
						{contents.title && (
							<h4 className={`_base bold`} dangerouslySetInnerHTML={{ __html: contents.title }} />
						)}
						{contents.body && (
							<div className={`_base`} dangerouslySetInnerHTML={{ __html: contents.body }} />
						)}
					</div>
				))}
			{credits && <span className={`_small`} dangerouslySetInnerHTML={{ __html: credits }} />}
			{button && button.label && <Button {...button} />}
		</div>
	);
};

const PlayButton = ({ url }) => {
	const track = React.useRef();
	const [playing, setPlaying] = React.useState();

	const toggle = () => {
		const play = () => {
			setPlaying(true);
			track.current.play();
		};

		const pause = () => {
			setPlaying(false);
			track.current.pause();
		};

		track.current.playing() ? pause() : play();
	};

	React.useEffect(() => {
		track.current = new Howl({
			src: url,
			html5: true,
			reload: true,
			onend: () => setPlaying(false),
		});

		return () => {
			track.current.stop();
		};
	}, [url]);

	return (
		<button onClick={toggle} className={`play-button`}>
			{playing ? <Pause /> : <Play />}
		</button>
	);
};

const List = ({ title, services, button }) => {
	return (
		<div className={`list`}>
			{title && <h3 className={`_big centered`} dangerouslySetInnerHTML={{ __html: title }} />}
			{services && (
				<div className={`list-grid`}>
					{services.length > 0 &&
						services.map((service, index) => (
							<div key={index} className={`list-grid-wrapper`}>
								{service.audio && <PlayButton {...service.audio} />}
								<div className={`list-grid-text`}>
									{service.title && (
										<h4
											className={`_base bold`}
											dangerouslySetInnerHTML={{ __html: service.title }}
										/>
									)}
									{service.body && (
										<div className={`_base`} dangerouslySetInnerHTML={{ __html: service.body }} />
									)}
								</div>
							</div>
						))}
				</div>
			)}
			{button.label && <Button {...button} />}
		</div>
	);
};

const Loghi = ({ loghi }) => {
	return (
		<div className={`griglia-loghi`}>
			{loghi &&
				loghi.length > 0 &&
				loghi.map((logo, index) => (
					<a key={index} className={`griglia-loghi-logo`} href={logo.link} target="_blank" rel="noopener noreferrer">
						<img src={logo.file.sizes.large} alt={logo.file.description} />
					</a>
				))}
		</div>
	);
};

const Newsletter = ({ initial, success, fail, sample_email, testo_bottone, gdpr_checkbox }) => {
	const [status, setStatus] = React.useState(undefined);
	const form = React.useRef();
	const { t } = useTranslation();

	const get = (event) => {
		event.preventDefault();

		$.ajax({
			type: "POST",
			url: `https://filoalto.us8.list-manage.com/subscribe/post-json?u=e49a1ab19a85cc5e28ba0bbd5&amp;id=80b4cb3681`,
			data: new URLSearchParams(Array.from(new FormData(form.current))).toString(),
			cache: false,
			dataType: "jsonp",
			jsonp: "c",
			error: (error) => {
				console.error(error);
			},
			success: (data) => {
				data.result === `success` ? setStatus(true) : setStatus(false);
			},
		});
	};

	return (
		<div className={`newsletter`}>
			<div className={`newsletter-inner`}>
				<div className={`newsletter-inner-content`}>
					<p
						className={[`newsletter-inner-content-title`, `_big`, `centered`].join(` `)}
						dangerouslySetInnerHTML={{
							__html: status === undefined ? initial.titolo : status ? success.titolo : fail.titolo,
						}}
					/>
					<div
						className={[`newsletter-inner-content-body`, `_base`, `centered`].join(` `)}
						dangerouslySetInnerHTML={{
							__html: status === undefined ? initial.body : status ? success.body : fail.body,
						}}
					/>
					{!status && (
						<form ref={form} onSubmit={get} className={`newsletter-inner-content-body-form`}>
							<div className={`newsletter-inner-content-body-form-input`}>
								<label className='newsletter-inner-content-body-form-input-email'>
									<input type={`email`} name={`EMAIL`} placeholder={sample_email} required />
								</label>
								<button className={`newsletter-inner-content-body-form-input-button`}>
									{testo_bottone}
								</button>
							</div>
							<label className='newsletter-inner-content-body-form-checkbox'>
								<span dangerouslySetInnerHTML={{ __html: gdpr_checkbox }} />
								<input type='checkbox' required />
								<span className='newsletter-inner-content-body-form-checkbox-checkmark'></span>
							</label>
						</form>
					)}
				</div>
				{!status && (
					<div className={`newsletter-inner-terms`}>
						<p className={`_small centered`}>
							{t("mailchimp_1")}{" "}
							<a
								href='https://www.iubenda.com/privacy-policy/28197277'
								target='_blank'
								rel='noreferrer'>
								{t("mailchimp_2")}
							</a>
							.
						</p>
						<p className={`_small centered`}>
							{t("mailchimp_3")}
							<a href='https://mailchimp.com/legal/' target='_blank' rel='noreferrer'>
								{t("mailchimp_4")}
							</a>
							.
						</p>
					</div>
				)}
			</div>
		</div>
	);
};

export const Contents = ({ contents }) => {
	return (
		<div>
			{contents.map((content, index) => {
				switch (content.acf_fc_layout) {
					case `soundstrato`:
						return <SoundStrato key={index} />;

					case `quote`:
						return <Quote key={index} {...content} />;

					case `paragraph`:
						return <Paragraph key={index} {...content} />;

					case `gallery`:
						return <Gallery key={index} {...content} />;

					case `list`:
						return <List key={index} {...content} />;

					case `griglia_loghi`:
						return <Loghi key={index} {...content} />;

					case `newsletter`:
						return <Newsletter key={index} {...content} />;

					default:
						return false;
				}
			})}
		</div>
	);
};
